import type { Message } from "../../types"
import { isOwnMessage } from "../../xmpp/stanzas/message"
import type { Jid } from "../../xmpp/jid"

const HOUR_MS = 60 * 60 * 1000

/**
 * Whether to OFFER the reader a retraction for this message. Each clause
 * mirrors one Costanza `Authz` check, so the menu never carries an action the
 * server would refuse:
 *
 * - own message      → `authored_by?`, else `:forbidden`
 * - not retracted    → `retracted?`, else `:already_retracted`
 * - inside `windowHours` → `fresh?`, else `:expired`
 *
 * The server is the authority (FAME-522): it evaluates the window against the
 * archive's stored timestamp, while we can only compare against the local
 * clock. A clock running behind therefore offers a retraction the server
 * refuses — which reverts through the bounce path — and one running ahead
 * hides the menu slightly early. Neither is corrected here: this decides what
 * to offer, a refusal decides what happens.
 *
 * Connectivity is deliberately not a clause. The caller nulls the whole
 * handler while offline, which keeps this pure.
 */
export function canRetract(
  message: Message,
  selfJid: Jid | undefined,
  windowHours: number | undefined,
  now: number
): boolean {
  // No session yet: no identity to compare against and no window to apply.
  if (!selfJid || windowHours === undefined) return false

  // Only a body can be retracted — not a tombstone, an event or a retraction.
  if (message.type !== "chat") return false

  return (
    isOwnMessage(message, selfJid) &&
    !message.retracted &&
    now - message.timestamp < windowHours * HOUR_MS
  )
}
